import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../components/layout/MainLayout";
import InjectionForm from "./InjectionForm";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import { fetchInjection, updateInjectionByCase } from "../../store/slices/injectionsSlice";

const EditInjection = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId: navCaseId, patientId: navPatientId } = state || {};
    const caseId = navCaseId || currentInjection?.case_id;
    const patientId = navPatientId || currentInjection?.patient_id;

    const { currentInjection, isLoading, error } = useSelector((state) => state.injections);
    const [formData, setFormData] = useState(null);
    const [errors, setErrors] = useState({});

    // Fetch injection by ID
    useEffect(() => {
        if (id) dispatch(fetchInjection(id));
    }, [dispatch, id]);

    // Populate form data
    useEffect(() => {
        if (currentInjection && currentInjection.patientInfo) {
            const info = currentInjection.patientInfo;
            const onDuty = info.on_duty_injections?.[0] || {};
            const rn = info.rn_injections?.[0] || {};

            setFormData({
                // Patient Info
                // patientType: info.patient_type || "Private Patient",
                departmentName: info.department_name,
                revisionNumber: info.revision_no,
                documentNumber: info.document_no,
                reviewDate: info.review_date,
                issueDate: info.issue_date,
                standardName: info.standard_name,
                patientName: info.name || "",
                patientGender: info.gender || "",
                patientDOB: info.dob ? info.dob.split('T')[0] : "",
                patientContact: info.contact || "",

                // On Duty Section
                serenace: onDuty.serenace || "No",
                serenaceDose: onDuty.serenace_dose || "",
                serenaceType: onDuty.serenace_type || "",
                quetiapine: onDuty.quetiapine || "",
                quetiapineDose: onDuty.quetiapine_dose || "",
                vallium: onDuty.vallium || "No",
                valliumDose: onDuty.vallium_dose || "",
                valliumType: onDuty.vallium_type || "",
                avil: onDuty.avil || "No",
                avilDose: onDuty.avil_dose || "",
                avilType: onDuty.avil_type || "",
                clopixol: onDuty.clopixol || "No",
                clopixolDose: onDuty.clopixol_dose || "",
                clopixolType: onDuty.clopixol_type || "",
                dormicum: onDuty.dormicum || "No",
                dormicumDose: onDuty.dormicum_dose || "",
                dormicumType: onDuty.dormicum_type || "",
                procyclidine: onDuty.procyclidine || "No",
                procyclidineDose: onDuty.procyclidine_dose || "",
                procyclidineType: onDuty.procyclidine_type || "",
                doctorRemarks: onDuty.doctor_remarks || "",
                doctorName: onDuty.doctor_name || "",
                doctorDesignation: onDuty.doctor_designation || "",
                doctorPlace: onDuty.doctor_place || "",

                // RN Section
                rnSerenace: rn.rn_serenace || "No",
                rnSerenaceDose: rn.rn_serenace_dose || "",
                rnSerenaceType: rn.rn_serenace_type || "",
                rnQuetiapine: rn.rn_quetiapine || "No",
                rnQuetiapineDose: rn.rn_quetiapine_dose || "",
                rnVallium: rn.rn_vallium || "No",
                rnValliumDose: rn.rn_vallium_dose || "",
                rnValliumType: rn.rn_vallium_type || "",
                rnAvil: rn.rn_avil || "No",
                rnAvilDose: rn.rn_avil_dose || "",
                rnAvilType: rn.rn_avil_type || "",
                rnClopixol: rn.rn_clopixol || "No",
                rnClopixolDose: rn.rn_clopixol_dose || "",
                rnClopixolType: rn.rn_clopixol_type || "",
                rnDormicum: rn.rn_dormicum || "No",
                rnDormicumDose: rn.rn_dormicum_dose || "",
                rnDormicumType: rn.rn_dormicum_type || "",
                rnProcyclidine: rn.rn_procyclidine || "No",
                rnProcyclidineDose: rn.rn_procyclidine_dose || "",
                rnProcyclidineType: rn.rn_procyclidine_type || "",
                rnRemarks: rn.rn_remarks || "",
                rnName: rn.rn_name || "",
                rnDesignation: rn.rn_designation || "",
                rnPlace: rn.rn_place || "",
                amount: info.amount || "",
                paymentStatus: info.payment_status || "",
                referenceNo: info.reference_no || "",
                paymentDate: info.payment_date || "",
            });
        }
    }, [currentInjection]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.patientName) newErrors.patientName = "Patient name is required";
        if (!formData.doctorName) newErrors.doctorName = "Doctor name is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const nullifyIfNo = (value, fields) => {
        if (value === "No") {
            const updated = {};
            fields.forEach(f => updated[f] = null);
            return updated;
        }
        return {};
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill all required fields.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        // Auto-null fields when drug is "No"
        const onDutyFix = {
            ...nullifyIfNo(formData.quetiapine, ["quetiapineDose"]),
            ...nullifyIfNo(formData.serenace, ["serenaceDose", "serenaceType"]),
            ...nullifyIfNo(formData.vallium, ["valliumDose", "valliumType"]),
            ...nullifyIfNo(formData.avil, ["avilDose", "avilType"]),
            ...nullifyIfNo(formData.clopixol, ["clopixolDose", "clopixolType"]),
            ...nullifyIfNo(formData.dormicum, ["dormicumDose", "dormicumType"]),
            ...nullifyIfNo(formData.procyclidine, ["procyclidineDose", "procyclidineType"]),
        };

        const rnFix = {
            ...nullifyIfNo(formData.rnQuetiapine, ["rnQuetiapineDose"]),
            ...nullifyIfNo(formData.rnSerenace, ["rnSerenaceDose", "rnSerenaceType"]),
            ...nullifyIfNo(formData.rnVallium, ["rnValliumDose", "rnValliumType"]),
            ...nullifyIfNo(formData.rnAvil, ["rnAvilDose", "rnAvilType"]),
            ...nullifyIfNo(formData.rnClopixol, ["rnClopixolDose", "rnClopixolType"]),
            ...nullifyIfNo(formData.rnDormicum, ["rnDormicumDose", "rnDormicumType"]),
            ...nullifyIfNo(formData.rnProcyclidine, ["rnProcyclidineDose", "rnProcyclidineType"]),
        };

        // Apply corrections
        Object.assign(formData, onDutyFix, rnFix);

        const payload = {
            case_id: caseId,
            patient_id: patientId,
            // patient_type: formData.patientType,
            department_name: formData.departmentName,
            revision_no: formData.revisionNumber,
            document_no: formData.documentNumber,
            review_date: formData.reviewDate,
            issue_date: formData.issueDate,
            standard_name: formData.standardName,
            name: formData.patientName,
            gender: formData.patientGender ? formData.patientGender.charAt(0).toUpperCase() + formData.patientGender.slice(1).toLowerCase() : null,
            dob: formData.patientDOB,
            contact: formData.patientContact,
            amount: formData.amount,
            payment_status: formData.paymentStatus,
            reference_no: formData.referenceNo,
            payment_date: formData.paymentDate,

            onDutyInjections: [
                {
                    quetiapine: formData.quetiapine,
                    quetiapine_dose: formData.quetiapineDose,
                    serenace: formData.serenace,
                    serenace_dose: formData.serenaceDose,
                    serenace_type: formData.serenaceType,
                    vallium: formData.vallium,
                    vallium_dose: formData.valliumDose,
                    vallium_type: formData.valliumType,
                    avil: formData.avil,
                    avil_dose: formData.avilDose,
                    avil_type: formData.avilType,
                    clopixol: formData.clopixol,
                    clopixol_dose: formData.clopixolDose,
                    clopixol_type: formData.clopixolType,
                    dormicum: formData.dormicum,
                    dormicum_dose: formData.dormicumDose,
                    dormicum_type: formData.dormicumType,
                    procyclidine: formData.procyclidine,
                    procyclidine_dose: formData.procyclidineDose,
                    procyclidine_type: formData.procyclidineType,
                    doctor_remarks: formData.doctorRemarks,
                    doctor_name: formData.doctorName,
                    doctor_designation: formData.doctorDesignation,
                    doctor_place: formData.doctorPlace,
                },
            ],

            rnInjections: [
                {
                    rn_quetiapine: formData.quetiapine,
                    rn_quetiapine_dose: formData.quetiapineDose,
                    rn_serenace: formData.rnSerenace,
                    rn_serenace_dose: formData.rnSerenaceDose,
                    rn_serenace_type: formData.rnSerenaceType,
                    rn_vallium: formData.rnVallium,
                    rn_vallium_dose: formData.rnValliumDose,
                    rn_vallium_type: formData.rnValliumType,
                    rn_avil: formData.rnAvil,
                    rn_avil_dose: formData.rnAvilDose,
                    rn_avil_type: formData.rnAvilType,
                    rn_clopixol: formData.rnClopixol,
                    rn_clopixol_dose: formData.rnClopixolDose,
                    rn_clopixol_type: formData.rnClopixolType,
                    rn_dormicum: formData.rnDormicum,
                    rn_dormicum_dose: formData.rnDormicumDose,
                    rn_dormicum_type: formData.rnDormicumType,
                    rn_procyclidine: formData.rnProcyclidine,
                    rn_procyclidine_dose: formData.rnProcyclidineDose,
                    rn_procyclidine_type: formData.rnProcyclidineType,
                    rn_remarks: formData.rnRemarks,
                    rn_name: formData.rnName,
                    rn_designation: formData.rnDesignation,
                    rn_place: formData.rnPlace,
                },
            ],
        };

        try {
            console.log("idS: ", caseId, patientId);
            await dispatch(updateInjectionByCase({ caseId, patientId, injectionData: payload })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Injection record updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            // Stay on edit page
        } catch (err) {
            Swal.fire("Error!", err || "Failed to update injection record.", "error");
        }
    };

    if (isLoading || !formData) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                {/* ✅ Top header with breadcrumbs and print button */}
                <div className="d-flex justify-content-between align-items-center">
                    <Breadcrumbs />
                    <Link
                        to={`/injections/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>

                <div className="card">
                    <div className="card-body">
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors).map((err, i) => (
                                    <div key={i}>{err}</div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <InjectionForm formData={formData} handleChange={handleChange} />

                            <div className="row">
                                <div className="col-md-2 ms-auto">
                                    <button type="submit" className="btn btn-primary w-100">
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditInjection;
